<template>
    <div class="order-detail-v1__header-stripe">
        <div class="row">
            <div class="col-md-6">
                <div class="order-detail-v1__header-lead">
                    <h1 class="order-detail-v1__header-title">
                        {{ order.identity.numPrenotazione }}
                        <span class="label label-portal text-uppercase">{{ portalLabel }}</span>
                    </h1>
                    <div v-if="canCancelOrder || canArchiveOrder || canSetNonArchivable" class="order-detail-v1__header-buttons">
                        <button
                            v-if="canCancelOrder"
                            type="button"
                            class="btn btn-sm btn-warning"
                            data-testid="order-detail-cancel-order"
                            :disabled="busy"
                            @click="cancelWholeOrder"
                        >
                            Annulla ordine (BeeStore)
                        </button>
                        <button
                            v-if="canArchiveOrder"
                            type="button"
                            class="btn btn-sm btn-default"
                            data-testid="order-detail-archive-order"
                            :disabled="busy || nonArchiviabile"
                            @click="archiveOrder"
                        >
                            Archivia ordine
                        </button>
                        <label
                            v-if="canSetNonArchivable"
                            class="checkbox-inline order-detail-v1__non-archiviabile"
                        >
                            <input
                                type="checkbox"
                                data-testid="order-detail-non-archiviabile"
                                :checked="nonArchiviabile"
                                :disabled="busy"
                                @change="toggleNonArchiviabile"
                            >
                            Non archiviabile
                        </label>
                    </div>
                </div>
            </div>
            <div class="col-md-6">
                <dl class="order-detail-v1__header-meta row">
                    <div class="col-sm-6 col-md-3">
                        <dt>Entity</dt>
                        <dd>#{{ order.identity.orderEntityId }}</dd>
                    </div>
                    <div class="col-sm-6 col-md-3">
                        <dt>Correlation ID</dt>
                        <dd><code>{{ order.identity.correlationId }}</code></dd>
                    </div>
                    <div class="col-sm-6 col-md-3">
                        <dt>Prenotato il</dt>
                        <dd>{{ formatDate(order.header.bookedAt) }}</dd>
                    </div>
                    <div class="col-sm-6 col-md-3 order-detail-v1__header-back">
                        <a v-if="backUrl" :href="backUrl" class="btn btn-show-back">
                            <i class="fa fa-arrow-left" aria-hidden="true"></i> Indietro
                        </a>
                    </div>
                </dl>
            </div>
        </div>
    </div>
</template>

<script>
import { formatDate } from './orderDetailHelpers';

export default {
    name: 'OrderDetailHeader',
    props: {
        order: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            busy: false,
            nonArchiviabileValue: null,
        };
    },
    computed: {
        portalLabel() {
            const portal = this.order.identity && this.order.identity.portal;
            if (!portal || String(portal).trim() === '') {
                return 'dlm';
            }

            return String(portal).toLowerCase();
        },
        backUrl() {
            return (this.order.endpoints && this.order.endpoints.index) || null;
        },
        canCancelOrder() {
            return !!(this.order.ui && this.order.ui.actions && this.order.ui.actions.canCancelOrder)
                && !!(this.order.endpoints && this.order.endpoints.cancelOrder);
        },
        canArchiveOrder() {
            return !!(this.order.ui && this.order.ui.actions && this.order.ui.actions.canArchiveOrder)
                && !!(this.order.endpoints && this.order.endpoints.archiveOrder);
        },
        canSetNonArchivable() {
            return !!(this.order.ui && this.order.ui.actions && this.order.ui.actions.canSetNonArchivable)
                && !!(this.order.endpoints && this.order.endpoints.setNonArchiviabile);
        },
        nonArchiviabile() {
            if (this.nonArchiviabileValue !== null) {
                return this.nonArchiviabileValue;
            }

            return !!(this.order.header && this.order.header.nonArchiviabile);
        },
    },
    methods: {
        formatDate,
        async toggleNonArchiviabile(event) {
            const next = !!(event && event.target && event.target.checked);
            const confirmed = await this.confirmNonArchiviabile(next);
            if (!confirmed) {
                event.target.checked = !next;
                return;
            }
            this.busy = true;
            try {
                const payload = await this.postJson(this.order.endpoints.setNonArchiviabile, {
                    non_archiviabile: next,
                }, 'PATCH');
                this.nonArchiviabileValue = !!payload.non_archiviabile;
                this.notify('success', payload.message || 'Flag aggiornato.');
            } catch (err) {
                event.target.checked = !next;
                this.notify('error', (err && err.message) || 'Operazione fallita.');
            } finally {
                this.busy = false;
            }
        },
        confirmNonArchiviabile(next) {
            const text = next
                ? 'Escludere questo ordine dall\'archivio automatico? Il pulsante Archivia verrà disabilitato.'
                : 'Rendere di nuovo archiviabile questo ordine?';

            return window.Swal.fire({
                text,
                icon: 'warning',
                showCancelButton: true,
                buttonsStyling: false,
                confirmButtonText: next ? 'Escludi' : 'Rendi archiviabile',
                cancelButtonText: 'Annulla',
                customClass: {
                    confirmButton: 'btn btn-warning',
                    cancelButton: 'btn btn-default',
                },
            }).then((result) => !!result.isConfirmed);
        },
        notify(icon, title) {
            const toast = window.SwalToast || window.Swal;
            if (!toast || !title) {
                return;
            }

            toast.fire({
                icon,
                title,
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000,
            });
        },
        async archiveOrder() {
            if (!window.confirm('Archiviare l\'ordine? Verrà spostato in archivio e rimosso dagli ordini attivi.')) {
                return;
            }
            this.busy = true;
            try {
                const payload = await this.postJson(this.order.endpoints.archiveOrder, {});
                if (payload.redirect_url) {
                    window.location.href = payload.redirect_url;
                    return;
                }
                this.notify('success', payload.message || 'Ordine archiviato.');
            } catch (err) {
                this.notify('error', (err && err.message) || 'Operazione fallita.');
            } finally {
                this.busy = false;
            }
        },
        async cancelWholeOrder() {
            if (!window.confirm('Annullare l\'intero ordine su BeeStore?')) {
                return;
            }
            this.busy = true;
            try {
                const payload = await this.postJson(this.order.endpoints.cancelOrder, { mode: 'annul_only' });
                if (payload.redirect_url) {
                    window.location.href = payload.redirect_url;
                    return;
                }
                this.notify('success', payload.message || 'Ordine aggiornato.');
                window.setTimeout(() => window.location.reload(), 600);
            } catch (err) {
                this.notify('error', (err && err.message) || 'Operazione fallita.');
            } finally {
                this.busy = false;
            }
        },
        async postJson(url, body, method = 'POST') {
            if (!url) {
                throw new Error('Endpoint non configurato.');
            }
            const response = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                    'X-CSRF-TOKEN': this.order.csrfToken || '',
                    'X-Requested-With': 'XMLHttpRequest',
                },
                body: JSON.stringify(body),
            });
            const payload = await response.json().catch(() => ({}));
            if (!response.ok || payload.success === false) {
                throw new Error(payload.message || ('HTTP ' + response.status));
            }
            return payload;
        },
    },
};
</script>
